// Cmd-K / Ctrl-K palette. Mounted once at the Shell level and
// toggled by a global keyboard listener so any page surface can
// open it. Debounced search (180ms after the last keystroke);
// arrow-key navigation between results; Enter to navigate to the
// active party; Escape to close.
//
// Sub-2-character queries hit early-exit on the server (the search
// endpoint returns empty for those) so we don't pound the DB on
// every single keystroke.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partyApi, type PartySearchResult } from '../services/partyApi';
import { SearchResultRow } from './SearchResultRow';

const DEBOUNCE_MS = 180;
const PALETTE_LIMIT = 20;

export function SearchPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PartySearchResult[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const reqIdRef = useRef(0);
  const nav = useNavigate();

  // Global Cmd-K / Ctrl-K listener. Attaches once to the document.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus the input when opened, clear state when closed.
  useEffect(() => {
    if (open) {
      // requestAnimationFrame so the input exists in the DOM first.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery('');
      setResults([]);
      setActive(0);
      setErr(null);
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    }
  }, [open]);

  // Debounced search. Each fire bumps reqIdRef so stale responses
  // from earlier keystrokes can't overwrite the newest results.
  const run = useCallback((q: string) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    partyApi.search(q, { limit: PALETTE_LIMIT })
      .then(r => {
        if (reqId !== reqIdRef.current) return; // stale
        setResults(r.results);
        setActive(0);
      })
      .catch(e => {
        if (reqId !== reqIdRef.current) return;
        setErr(e?.message ?? 'Search failed');
      })
      .finally(() => {
        if (reqId !== reqIdRef.current) return;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    debounceRef.current = window.setTimeout(() => run(query.trim()), DEBOUNCE_MS);
  }, [query, open, run]);

  function pickAndClose(r: PartySearchResult) {
    setOpen(false);
    nav(`/mi/party/${r.party_id}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, Math.max(0, results.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[active];
      if (r) pickAndClose(r);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 pt-[8vh]" onClick={() => setOpen(false)}>
      <div className="bg-bg-card border border-border rounded-lg w-[680px] max-w-[94vw] max-h-[70vh] overflow-hidden flex flex-col shadow-xl"
        onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <span className="text-text-faint text-[14px]">⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search parties — name, alias, or phone digits"
            className="flex-1 bg-transparent border-none outline-none text-[14px] text-text placeholder:text-text-faint"
          />
          <span className="text-[10px] font-mono text-text-faint">ESC</span>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {err && (
            <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2 m-2">{err}</div>
          )}
          {!err && query.trim().length < 2 && (
            <div className="text-[12px] text-text-faint font-mono px-3 py-8 text-center">
              Type 2+ characters to search across 92k+ parties + aliases.<br />
              <span className="text-text-ghost">Digit-heavy queries also match phone numbers.</span>
            </div>
          )}
          {!err && query.trim().length >= 2 && results.length === 0 && !loading && (
            <div className="text-[12px] text-text-faint font-mono px-3 py-8 text-center">no matches</div>
          )}
          {!err && loading && results.length === 0 && (
            <div className="text-[12px] text-text-faint font-mono px-3 py-8 text-center">searching…</div>
          )}
          <div className="space-y-0.5">
            {results.map((r, i) => (
              <SearchResultRow
                key={r.party_id}
                result={r}
                active={i === active}
                onSelect={() => pickAndClose(r)}
              />
            ))}
          </div>
        </div>
        <div className="px-4 py-2 border-t border-border bg-bg-card-soft/40 text-[10px] font-mono text-text-faint flex items-center gap-4">
          <span><kbd className="px-1 bg-bg-input border border-border rounded">↑</kbd> <kbd className="px-1 bg-bg-input border border-border rounded">↓</kbd> navigate</span>
          <span><kbd className="px-1 bg-bg-input border border-border rounded">enter</kbd> open</span>
          <span><kbd className="px-1 bg-bg-input border border-border rounded">esc</kbd> close</span>
          <span className="ml-auto">{loading ? 'searching…' : `${results.length} ${results.length === 1 ? 'result' : 'results'}`}</span>
        </div>
      </div>
    </div>
  );
}
